/**
 * Form Data Utilities
 *
 * Shared utilities for form data processing and validation
 */

import { ExtractedField } from '../types/extractedData';
import { MASK_LENGTH } from '../constants/uiConfig';

/**
 * Form field structure
 */
export interface IFormField {
  label: string;
  value: string;
  isVertical: boolean;
  fieldType?: string;
  /** Per-field extraction confidence (0–100) when the API provided it. */
  confidence?: number;
  /** Optional alternate values from extraction (e.g. suggestions, alternatives). */
  suggestions?: string[];
  /** Path of string keys from extractedData root to this field (for persisting edits). */
  persistPath?: string[];
  /** Source-location evidence returned by extraction for provenance reporting. */
  sourceEvidence?: FieldSourceEvidence;
}

export interface FieldSourceEvidence {
  fileName?: string | null;
  filename?: string | null;
  pageNumber?: number | string | null;
  page?: number | string | null;
  sectionHeading?: string | null;
  section?: string | null;
  paragraph?: string | null;
  snippet?: string | null;
  sourceText?: string | null;
}

/**
 * Form subsection structure
 */
export interface IFormSubsection {
  title: string;
  fields: IFormField[];
}

/**
 * Form section structure
 */
export interface IFormSection {
  id: string;
  title: string;
  subsections: IFormSubsection[];
}

/**
 * Interface for API responses that contain extracted data
 */
interface ApiResponseWithExtractedData {
  extractedData?: unknown;
}

/**
 * Type guard to check if a value is an ExtractedField
 */
export const isExtractedField = (value: unknown): value is ExtractedField => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  const valObj = value as Record<string, any>;
  return 'value' in valObj || 'Value' in valObj;
};

const SUGGESTION_KEYS = [
  'suggestions',
  'Suggestions',
  'alternatives',
  'Alternatives',
  'candidates',
  'Candidates',
] as const;

function isNonArrayObject(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

function stringOrNullFromSuggestionItem(item: unknown): string | null {
  if (typeof item === 'string') {
    const t = item.trim();
    return t ? t : null;
  }
  if (!isNonArrayObject(item)) {
    return null;
  }
  const v = item.value ?? item.Value;
  if (v == null) {
    return null;
  }
  const t = String(v).trim();
  return t || null;
}

function collectTrimmedStringsFromList(arr: unknown): string[] {
  if (!Array.isArray(arr)) {
    return [];
  }
  const out: string[] = [];
  for (const item of arr) {
    const s = stringOrNullFromSuggestionItem(item);
    if (s) {
      out.push(s);
    }
  }
  return out;
}

/**
 * Read string suggestions from an extracted-field object (if the API sends them).
 */
export function extractSuggestionsFromExtractedValue(value: unknown): string[] {
  if (!isNonArrayObject(value)) {
    return [];
  }
  for (const k of SUGGESTION_KEYS) {
    const out = collectTrimmedStringsFromList(value[k]);
    if (out.length > 0) {
      return out;
    }
  }
  return [];
}

/** Match DocumentRenderer field display formatting for modal initial value. */
export function formatFormFieldDisplayValue(value: string, fieldType?: string): string {
  if (!value || value === '-') return '-';

  switch (fieldType) {
    case 'boolean': {
      const boolStr = String(value).toLowerCase();
      return boolStr === 'true' || boolStr === '1' || boolStr === 'yes' ? 'Yes' : 'No';
    }
    default:
      return value;
  }
}

/**
 * Detect if a string appears to be JSON
 */
export const looksLikeJsonString = (value: string): boolean => {
  const trimmed = value.trim();
  return (
    (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
    (trimmed.startsWith('[') && trimmed.endsWith(']'))
  );
};

/**
 * Extract JSON content from possible fenced code blocks
 */
export const stripCodeFences = (value: string): string => {
  const trimmed = value.trim();
  const fencedMatch = /^```(?:json)?\n([\s\S]*?)\n```$/i.exec(trimmed);
  if (fencedMatch?.[1]) {
    return fencedMatch[1].trim();
  }
  return value;
};

/**
 * Determine if a value is meaningful enough to render
 * This is the robust implementation that handles edge cases like:
 * - Empty strings and whitespace-only strings
 * - String-encoded JSON (including fenced code blocks)
 * - Arrays with null/undefined values
 * - Objects with no meaningful nested values
 */
export const isRenderableValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return false;

  // Handle ExtractedField structure { value: string, confidence: number }
  if (isExtractedField(value)) {
    const actualValue = value.value !== undefined ? value.value : value.Value;
    return isRenderableValue(actualValue);
  }

  if (typeof value === 'string') {
    return isRenderableStringValue(value);
  }

  if (Array.isArray(value)) {
    // Renderable if any element is renderable
    return value.some((v) => isRenderableValue(v));
  }

  if (typeof value === 'object') {
    // Renderable if any nested value is renderable
    return Object.values(value).some((v) => isRenderableValue(v));
  }

  // numbers, booleans are renderable (0 and false are valid)
  return true;
};

function isRenderableStringValue(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed.length === 0) return false;
  if (['null', 'undefined', '{}', '[]'].includes(trimmed.toLowerCase())) return false;

  const unfenced = stripCodeFences(trimmed);
  if (looksLikeJsonString(unfenced)) {
    try {
      const parsed = JSON.parse(unfenced);
      return isRenderableValue(parsed);
    } catch {
      // fallthrough: treat as plain non-empty string
    }
  }
  return true;
}

/**
 * Check if an API response has any renderable form data
 * This function determines whether the extracted data contains
 * meaningful information that should be displayed in a form
 */
export const hasRenderableData = (apiResponse?: ApiResponseWithExtractedData): boolean => {
  if (!apiResponse || typeof apiResponse !== 'object') return false;

  const data = apiResponse.extractedData;
  if (!data || typeof data !== 'object') return false;

  if (Array.isArray(data)) {
    return data.some((v) => isRenderableValue(v));
  }

  return Object.values(data).some((v) => isRenderableValue(v));
};

/**
 * Get a nested value from an object using a dot-separated path
 * Handles ExtractedField structure { value: string, confidence: number }
 */
export const getNestedValue = (data: any, path: string): string => {
  if (!path || !data) return '';

  const keys = path.split('.');
  let current: any = data;

  for (const key of keys) {
    if (current && typeof current === 'object' && key in current) {
      current = current[key];
    } else {
      return '';
    }
  }

  if (current === null || current === undefined) {
    return '';
  }

  // Handle ExtractedField structure { value: string, confidence: number }
  if (isExtractedField(current)) {
    const val = current.value !== undefined ? current.value : current.Value;
    if (val === null || val === undefined) return '';
    return String(val);
  }

  if (typeof current === 'string') {
    return current;
  }
  if (typeof current === 'number' || typeof current === 'boolean') {
    return String(current);
  }

  return '';
};

/**
 * Format a form field value for display based on its label and content
 * Handles currency, percentage, and boolean formatting based on label keywords
 * @param field - The form field to format
 * @returns The formatted value as a string
 */
export const getFormattedFieldValue = (field: IFormField): string => {
  if (!field.value) return '-';

  // Format value based on field label (for display only, not stored)
  const lowerLabel = field.label.toLowerCase();

  // Currency fields: Display raw value from backend without client-side formatting.
  // The backend is trusted to provide properly formatted currency values.
  if (
    lowerLabel.includes('amount') ||
    lowerLabel.includes('price') ||
    lowerLabel.includes('cost') ||
    lowerLabel.includes('balance')
  ) {
    return field.value;
  }

  // Check for percentage/rate-related labels
  if (lowerLabel.includes('rate') || lowerLabel.includes('percent')) {
    return field.value;
  }

  // Check for boolean values (including numeric representations)
  if (typeof field.value === 'string' || typeof field.value === 'number') {
    const valStr = String(field.value).trim().toLowerCase();
    if (['true', 'yes', '1'].includes(valStr)) {
      return 'Yes';
    }
    if (['false', 'no', '0'].includes(valStr)) {
      return 'No';
    }
  }

  return field.value;
};

/**
 * Masks sensitive PII information
 * @param value - The value to mask
 * @returns The masked value
 */
export const maskPIIValue = (value: string): string => {
  if (!value || value === '-') return '-';

  return '*'.repeat(MASK_LENGTH);
};

/**
 * Escapes common HTML entities to prevent XSS and ensure data integrity
 * when interpolating text into HTML strings.
 * @param text - The text to escape
 * @returns The escaped text
 */
export const escapeHTML = (text: string): string => {
  if (!text) return '';
  return text
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
};
